// Money-room HUD (money-mode §4, §7; owner exception (c) for the hint): only during a money life.
// - Value box above the score box, same style: "Value: $0.90" (USD by default, SOL with the label
//   setting; the price comes from the room's FRAME MONEY section).
// - Under it, a small line: "You can cash out in {s}s" for the first `minLifeSec` (app.ts
//   `secondsUntilCashOut`), else "Hold Q to cash out" (the key from WELCOME `cashOutKey`).
// - While the own hold runs: a bottom-centre bar in the spectate-bar style, "Cashing out… 62%",
//   with a gold fill animated locally from `store.ownCashOutProgress(now)` (the server runs the
//   timer; a split or eject cancels it).
// - Touch devices: a 64 px "$" hold button above the eject button. Pressing it calls
//   `onHold(true)` (CASH_OUT_START via app.setCashOutHeld), releasing `onHold(false)`; its ring
//   fills with the progress.
//
// - Practice rooms (money-mode §1.4): a PRACTICE badge over the value box and silver values.
//
// `moneyHudView` is DOM-free (unit-tested). Consumer: app.ts (calls `update` every frame).

import type { LabelCurrency } from '@ballio/shared';
import { formatMoney } from '../../game/world-store.js';
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';

export interface MoneyHudInput {
  /** Own value in lamports (null = not known yet). */
  readonly value: number | null;
  readonly currency: LabelCurrency;
  /** USD per SOL (0 = unknown: USD labels fall back to SOL). */
  readonly solUsd: number;
  /** Own cash-out progress 0–1, or null when not holding. */
  readonly progress: number | null;
  /** WELCOME cashOutKey ("Q"). */
  readonly key: string;
  /** Seconds until cash-out is allowed (≤ 0 = allowed). */
  readonly secondsUntilCashOut: number;
  readonly touch: boolean;
  /** Practice room (money-mode §1.4): the PRACTICE badge and silver styling. Absent = false. */
  readonly practice?: boolean;
}

export interface MoneyHudView {
  readonly valueText: string;
  /** The line under the value box. */
  readonly hintText: string;
  /** 0–1 while holding, else null (bar hidden). */
  readonly progress: number | null;
  readonly progressText: string | null;
  /** "PRACTICE" in practice rooms, else null. */
  readonly badge?: string | null;
}

/** What the money HUD shows for one frame. */
export function moneyHudView(m: MoneyHudInput): MoneyHudView {
  const valueText = t('money.value', {
    v: m.value === null ? '…' : formatMoney(m.value, m.currency, m.solUsd),
  });
  const wait = Math.ceil(m.secondsUntilCashOut);
  const hintText =
    wait > 0
      ? t('money.tooEarly', { s: wait })
      : m.touch
        ? t('money.holdTouch')
        : t('money.holdHint', { key: m.key || 'Q' });
  const badge = m.practice === true ? t('practice.badge') : null;
  if (m.progress === null) {
    return { valueText, hintText, progress: null, progressText: null, badge };
  }
  const p = Math.min(1, Math.max(0, m.progress));
  return {
    valueText,
    hintText,
    progress: p,
    progressText: t('money.cashingOut', { pct: Math.floor(p * 100) }),
    badge,
  };
}

export interface MoneyHudDeps {
  /** The touch hold button went down (true) or up (false). */
  onHold(held: boolean): void;
}

export interface MoneyHud {
  /** Value box + hint (bottom-left), cash-out bar (bottom-centre), hold button (bottom-right). */
  readonly els: readonly HTMLElement[];
  /** null hides everything (not in a money life). */
  update(input: MoneyHudInput | null): void;
}

export function createMoneyHud(deps: MoneyHudDeps): MoneyHud {
  const value = h('div', { class: 'hud-box hud-money-value' });
  const hint = h('div', { class: 'hud-money-hint' });
  const badge = h('span', { class: 'money-tag hud-practice-badge', hidden: true });
  const left = h('div', { class: 'hud-money', hidden: true }, value, hint, badge);
  const fill = h('span', { class: 'hud-cashout-fill' });
  const barText = h('span', { class: 'hud-cashout-text' });
  const bar = h(
    'div',
    { class: 'hud-cashout', role: 'status', hidden: true },
    h('span', { class: 'hud-cashout-track' }, fill),
    barText,
  );
  let held = false;
  const release = (ev?: Event): void => {
    if (ev?.cancelable) ev.preventDefault();
    if (!held) return;
    held = false;
    holdBtn.classList.remove('is-held');
    deps.onHold(false);
  };
  const holdBtn = h(
    'button',
    {
      type: 'button',
      class: 'hud-hold-btn',
      'aria-label': t('money.holdTouch'),
      hidden: true,
      onPointerdown: (ev: PointerEvent) => {
        if (ev.cancelable) ev.preventDefault();
        if (held) return;
        held = true;
        holdBtn.classList.add('is-held');
        try {
          holdBtn.setPointerCapture(ev.pointerId);
        } catch {
          // Synthetic events have no active pointer.
        }
        deps.onHold(true);
      },
      onPointerup: release,
      onPointercancel: release,
      onLostpointercapture: () => release(),
      onContextmenu: (ev: Event) => ev.preventDefault(),
    },
    h('span', { class: 'hud-hold-glyph', 'aria-hidden': 'true' }, '$'),
  );

  let last = { valueText: '', hintText: '', progressText: '', pct: -1, visible: false };
  let touchShown = false;

  return {
    els: [left, bar, holdBtn],
    update(input: MoneyHudInput | null): void {
      if (input === null) {
        if (last.visible) {
          last = { ...last, visible: false };
          show(left, false);
          show(bar, false);
          show(holdBtn, false);
          touchShown = false;
          release();
        }
        return;
      }
      const view = moneyHudView(input);
      const practice = view.badge != null;
      if (left.classList.contains('is-practice') !== practice) {
        left.classList.toggle('is-practice', practice);
        bar.classList.toggle('is-practice', practice);
        badge.textContent = view.badge ?? '';
        show(badge, practice);
      }
      if (!last.visible) {
        last = { ...last, visible: true };
        show(left, true);
      }
      if (touchShown !== input.touch) {
        touchShown = input.touch;
        show(holdBtn, input.touch);
      }
      if (view.valueText !== last.valueText) {
        last.valueText = view.valueText;
        value.textContent = view.valueText;
      }
      if (view.hintText !== last.hintText) {
        last.hintText = view.hintText;
        hint.textContent = view.hintText;
      }
      const pct = view.progress === null ? -1 : Math.round(view.progress * 1000) / 10;
      if (pct !== last.pct) {
        last.pct = pct;
        show(bar, pct >= 0);
        const p = `${Math.max(0, pct)}%`;
        fill.style.width = p;
        holdBtn.style.setProperty('--p', p);
      }
      const text = view.progressText ?? '';
      if (text !== last.progressText) {
        last.progressText = text;
        barText.textContent = text;
      }
    },
  };
}
